import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminAPI } from '../lib/api';
import { Save, RotateCcw, Mail } from 'lucide-react';

const KEY_LABELS: Record<string, string> = {
  verify_email: 'Email verification',
  password_reset: 'Password reset',
  magic_link: 'Magic link',
  mfa_otp: 'MFA one-time code',
  new_login: 'New sign-in alert',
  invite: 'Organization invite',
};

const EmailTemplatesPage: React.FC = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['email-templates'], queryFn: () => adminAPI.getEmailTemplates() });
  const [activeKey, setActiveKey] = useState('password_reset');
  const [locale, setLocale] = useState('en');
  const [draft, setDraft] = useState<{ subject: string; html: string; text: string } | null>(null);

  const keys: string[] = data?.keys || [];
  const locales: string[] = data?.locales || ['en'];
  const override = data?.overrides?.[activeKey];
  const def = data?.defaults?.[activeKey]?.[locale] || data?.defaults?.[activeKey]?.en;
  const current = draft || override || def || { subject: '', html: '', text: '' };

  const save = useMutation({
    mutationFn: () => adminAPI.saveEmailTemplate(activeKey, current),
    onSuccess: () => { setDraft(null); qc.invalidateQueries({ queryKey: ['email-templates'] }); },
  });
  const reset = useMutation({
    mutationFn: () => adminAPI.resetEmailTemplate(activeKey),
    onSuccess: () => { setDraft(null); qc.invalidateQueries({ queryKey: ['email-templates'] }); },
  });

  const pick = (k: string) => { setActiveKey(k); setDraft(null); };
  const edit = (patch: Partial<{ subject: string; html: string; text: string }>) =>
    setDraft({ ...current, ...patch });

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-1 flex items-center"><Mail className="w-6 h-6 mr-2" />Email Templates</h1>
      <p className="text-gray-600 mb-6">Customize transactional emails. Overrides apply to your organization; defaults are localized (use <code>{'{{var}}'}</code> placeholders like <code>{'{{url}}'}</code>).</p>

      <div className="flex gap-6">
        <div className="w-56 shrink-0 space-y-1">
          {keys.map((k) => (
            <button key={k} onClick={() => pick(k)} className={`block w-full text-left px-3 py-2 rounded-md text-sm ${activeKey === k ? 'bg-blue-600 text-white' : 'hover:bg-gray-100'}`}>
              {KEY_LABELS[k] || k}
              {data?.overrides?.[k] && <span className="ml-2 text-xs opacity-75">(custom)</span>}
            </button>
          ))}
        </div>

        <div className="flex-1 bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">{KEY_LABELS[activeKey] || activeKey}</h2>
            <select value={locale} onChange={(e) => { setLocale(e.target.value); setDraft(null); }} className="border border-gray-300 rounded-md px-2 py-1 text-sm" disabled={!!override}>
              {locales.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
            </select>
          </div>
          {override && <p className="text-xs text-amber-600 mb-3">A custom override is active (locale selector applies only to defaults).</p>}

          <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
          <input value={current.subject} onChange={(e) => edit({ subject: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 mb-4" />

          <label className="block text-sm font-medium text-gray-700 mb-1">HTML body</label>
          <textarea value={current.html} onChange={(e) => edit({ html: e.target.value })} rows={8} className="w-full border border-gray-300 rounded-md px-3 py-2 mb-4 font-mono text-sm" />

          <label className="block text-sm font-medium text-gray-700 mb-1">Plain-text body</label>
          <textarea value={current.text} onChange={(e) => edit({ text: e.target.value })} rows={3} className="w-full border border-gray-300 rounded-md px-3 py-2 mb-4 font-mono text-sm" />

          <div className="flex gap-2">
            <button onClick={() => save.mutate()} disabled={save.isLoading} className="bg-blue-600 text-white px-4 py-2 rounded-md flex items-center disabled:opacity-50"><Save className="w-4 h-4 mr-1" />Save override</button>
            {override && <button onClick={() => reset.mutate()} className="px-4 py-2 border border-gray-300 rounded-md flex items-center"><RotateCcw className="w-4 h-4 mr-1" />Reset to default</button>}
          </div>

          <div className="mt-6">
            <p className="text-sm font-medium text-gray-700 mb-1">Preview</p>
            <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
              <p className="text-xs text-gray-500 mb-2">Subject: {current.subject}</p>
              <div className="bg-white p-3 rounded" dangerouslySetInnerHTML={{ __html: current.html }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplatesPage;
